import { z } from 'zod';
import { DOCUMENT_COMPLIANCE_STATUSES } from './configurable-engine.js';
import { CHECKLIST_COMPLIANCE_STATUSES, FLEET_COMPLIANCE_LEVELS } from './fleet-compliance.js';

/**
 * dashboard.ts
 * GET /servicios/dashboard — LOCX "Dashboard" landing view (add-locx-dashboard).
 *
 * Aggregates real data already produced by fleet-qr-compliance (semáforo,
 * document/checklist status) + fleet-failure-reports + the service_order FSM.
 * Every field here has a real backend source — see GetLocxDashboardUseCase's
 * header comment for the full real-vs-adapted breakdown (two reference-design
 * widgets were deliberately adapted, not dropped silently, because the exact
 * shape shown in the reference screenshot doesn't map onto this module's
 * closed vocabularies — see that comment before changing this shape).
 */

// ---------------------------------------------------------------------------
// Estado general de flota — derived from complianceLevel (verde/amarillo/rojo)
// + asset.status (active/inactive/maintenance). 'outOfService' takes priority
// over complianceLevel — a unit under maintenance isn't "critical", it's
// simply not in the fleet right now.
// ---------------------------------------------------------------------------

export const FLEET_STATUS_BUCKETS = ['ok', 'attention', 'critical', 'outOfService'] as const;
export type FleetStatusBucket = (typeof FLEET_STATUS_BUCKETS)[number];

export const FleetStatusCountsSchema = z.object({
  ok: z.number().int().nonnegative(),
  attention: z.number().int().nonnegative(),
  critical: z.number().int().nonnegative(),
  outOfService: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type FleetStatusCounts = z.infer<typeof FleetStatusCountsSchema>;

// ---------------------------------------------------------------------------
// Servicios por estado — service_order FSM states bucketed for the dashboard.
// 'drafts' (DRAFT) is shown as "Borradores", not "Planeados" — DRAFT means
// not-yet-submitted, there is no separate "scheduled" concept in this FSM.
// ---------------------------------------------------------------------------

export const ServiceStateCountsSchema = z.object({
  drafts: z.number().int().nonnegative(),
  pendingStart: z.number().int().nonnegative(),
  inProgress: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  cancelled: z.number().int().nonnegative(),
});
export type ServiceStateCounts = z.infer<typeof ServiceStateCountsSchema>;

export const DailyServiceCountSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  count: z.number().int().nonnegative(),
});
export type DailyServiceCount = z.infer<typeof DailyServiceCountSchema>;

// ---------------------------------------------------------------------------
// Próximos vencimientos — forward-looking only ([hoy, hoy+30]), cumulative
// buckets (in30Days includes in7Days includes today), same posture as NOMI's
// upcomingDocumentExpirations. Checklist has no per-day due date (period_key
// is calendar-aligned, not a single date) — checklistOverdueCount is a single
// "needs attention now" count, not a 3-bucket forecast.
// ---------------------------------------------------------------------------

export const UpcomingExpirationBucketsSchema = z.object({
  today: z.number().int().nonnegative(),
  in7Days: z.number().int().nonnegative(),
  in30Days: z.number().int().nonnegative(),
});
export type UpcomingExpirationBuckets = z.infer<typeof UpcomingExpirationBucketsSchema>;

export const CriticalAlertsSchema = z.object({
  expiredDocuments: z.number().int().nonnegative(),
  expiringToday: z.number().int().nonnegative(),
  overdueChecklists: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type CriticalAlerts = z.infer<typeof CriticalAlertsSchema>;

// ---------------------------------------------------------------------------
// Flota por ubicación — 'locality' is the RAW closed-catalog asset attribute
// value (asset_attribute_def key='localidad', data_type='select', e.g.
// 'anelo') — the human label + map coordinates are a presentation concern,
// resolved frontend-side (same posture as genderDistribution's raw 'M'/'F'
// bucket in @suite/contracts-rrhh). Assets with no value set are counted in
// fleetUnassignedLocalityCount, never silently dropped or guessed.
// ---------------------------------------------------------------------------

export const FleetLocalityCountSchema = z.object({
  locality: z.string(),
  count: z.number().int().nonnegative(),
});
export type FleetLocalityCount = z.infer<typeof FleetLocalityCountSchema>;

/** Per-vehicle detail — ONLY assets with a locality value (i.e. the ones that get a marker on the map). Backs "buscar por vehículo" in FleetByLocalityCard. */
export const FleetLocalityVehicleSchema = z.object({
  assetId: z.string().uuid(),
  serialNumber: z.string(),
  locality: z.string(),
});
export type FleetLocalityVehicle = z.infer<typeof FleetLocalityVehicleSchema>;

// ---------------------------------------------------------------------------
// Vehículos por CC (25 jul 2026) — costCenterId is a real, direct column on
// the asset (migration 0116; confirmed against the legacy that it's a stable
// field on the vehicle, not derived from its service orders). Names are
// batch-resolved server-side (kernel's ICatalogQuery.lookupCostCenterById),
// same posture as legalEntityName above. Assets with no cost center are
// counted in fleetUnassignedCostCenterCount, never silently dropped.
// ---------------------------------------------------------------------------

export const FleetCostCenterCountSchema = z.object({
  costCenterId: z.string().uuid(),
  costCenterName: z.string(),
  count: z.number().int().nonnegative(),
});
export type FleetCostCenterCount = z.infer<typeof FleetCostCenterCountSchema>;

// ---------------------------------------------------------------------------
// Unidades con atención requerida — same per-asset shape as FleetComplianceRow
// (fleet-compliance.ts) plus legalEntityName (Razón Social, batch-resolved via
// kernel's lookupCompanyNames — real data, replaces the reference design's
// "Operador" column, which has no backing concept in this domain).
// ---------------------------------------------------------------------------

export const AttentionRequiredRowSchema = z.object({
  assetId: z.string().uuid(),
  serialNumber: z.string(),
  description: z.string().nullable(),
  complianceLevel: z.enum(FLEET_COMPLIANCE_LEVELS),
  documentStatus: z.enum(DOCUMENT_COMPLIANCE_STATUSES),
  checklistStatus: z.enum(CHECKLIST_COMPLIANCE_STATUSES),
  lastChecklistSubmittedAt: z.string().datetime().or(z.date()).nullable(),
  legalEntityName: z.string().nullable(),
  /** Names of vehicle_document_type currently porVencer/vencido for this asset — drives cross-filtering from the "Resumen de vencimientos por tipo" chart. Empty when the asset's only problem is the checklist. */
  problemDocumentTypes: z.array(z.string()),
});
export type AttentionRequiredRow = z.infer<typeof AttentionRequiredRowSchema>;

// ---------------------------------------------------------------------------
// Resumen de vencimientos por tipo de documento — forward-only [hoy, hoy+30],
// same window as documentsUpcomingExpirations.in30Days, grouped by
// vehicle_document_type instead of bucketed by day. Mirrors NOMI's
// upcomingDocumentExpirations (contracts-rrhh dashboard.ts).
// ---------------------------------------------------------------------------

export const DocumentTypeExpirationCountSchema = z.object({
  documentTypeName: z.string(),
  count: z.number().int().nonnegative(),
});
export type DocumentTypeExpirationCount = z.infer<typeof DocumentTypeExpirationCountSchema>;

// "Documentos próximos a vencer" — one row per vehicle_document instance in
// the same [hoy, hoy+30] window (never 'sinVencimiento' — those have no
// expiresAt to windowed-filter on).
export const UpcomingDocumentRowSchema = z.object({
  assetId: z.string().uuid(),
  serialNumber: z.string(),
  documentTypeName: z.string(),
  expiresAt: z.string(),
  status: z.enum(DOCUMENT_COMPLIANCE_STATUSES),
});
export type UpcomingDocumentRow = z.infer<typeof UpcomingDocumentRowSchema>;

// ---------------------------------------------------------------------------
// Consumos de combustible (24 jul 2026 follow-up, redesigned same day to 3
// KPIs: consumo diario / top 10 consumidores / consumo total) — every field
// uses EVERY fuel_load row, liters-only, no odometer dependency (an earlier
// version scoped a L/km metric to the subset with a real odometer reading;
// dropped per user request in favor of always-reliable liters KPIs).
// Comparison window = "mismo rango de días" vs. the previous month (user
// decision, 24 jul 2026) — e.g. today=24 jul → current=[1 jul, 24 jul],
// previous=[1 jun, 24 jun]. No "meta mensual" (target) field — no such
// config exists anywhere in the system; omitted rather than fabricated.
// ---------------------------------------------------------------------------

export const DailyLitersSchema = z.object({
  date: z.string(), // YYYY-MM-DD
  liters: z.number().nonnegative(),
});
export type DailyLiters = z.infer<typeof DailyLitersSchema>;

export const FuelTopConsumerSchema = z.object({
  assetId: z.string().uuid(),
  serialNumber: z.string(),
  liters: z.number().nonnegative(),
});
export type FuelTopConsumer = z.infer<typeof FuelTopConsumerSchema>;

export const FuelConsumptionSummarySchema = z.object({
  totalLiters: z.number().nonnegative(),
  /** null when the previous comparison period had 0 liters (nothing to compare against). */
  totalLitersChangePercent: z.number().nullable(),
  dailyLiters: z.array(DailyLitersSchema),
  dailyAverageLiters: z.number().nonnegative(),
  /** Top 10 receiver assets by total liters in the period, descending. */
  topConsumers: z.array(FuelTopConsumerSchema),
});
export type FuelConsumptionSummary = z.infer<typeof FuelConsumptionSummarySchema>;

// ---------------------------------------------------------------------------
// Disponibilidad de flota (2026-08-28) — per-asset rental/reservation/
// maintenance status, a 7-day busy/free grid, and "próxima disponibilidad".
// Derived from asset.status + service_order_asset.periodFrom/periodTo — no
// new schema (domain/fleet-availability.ts owns the pure derivation; see its
// header comment for the status precedence rule and the "no exclusivity
// validation exists yet" caveat).
// ---------------------------------------------------------------------------

export const FLEET_AVAILABILITY_STATUSES = ['disponible', 'alquilado', 'reservado', 'mantenimiento'] as const;
export type FleetAvailabilityStatus = (typeof FLEET_AVAILABILITY_STATUSES)[number];

/** A raw rental period clipped to nothing — start/end as-is, may extend outside the requested year (the frontend clips to month/year bounds itself when deriving the grid). Both bounds always set — no open-ended period exists in this domain (ServiceOrderAssetRepository's "both bounds set" contract). */
export const FleetAvailabilityPeriodSchema = z.object({
  /** 'YYYY-MM-DD' */
  start: z.string(),
  /** 'YYYY-MM-DD' */
  end: z.string(),
});
export type FleetAvailabilityPeriod = z.infer<typeof FleetAvailabilityPeriodSchema>;

export const FleetAvailabilityAssetRowSchema = z.object({
  assetId: z.string().uuid(),
  serialNumber: z.string(),
  /** Human-readable label (2026-08-28: serialNumber alone, e.g. "CHK-001-01", reads as an opaque code). Null when the asset has none set. */
  description: z.string().nullable(),
  status: z.enum(FLEET_AVAILABILITY_STATUSES),
  /** Rental periods overlapping FleetAvailabilitySummary.year (2026-08-30 year-overview redesign — replaces the old per-day `days: boolean[]` 30-day grid). */
  periods: z.array(FleetAvailabilityPeriodSchema),
  /** 'YYYY-MM-DD', or 'ahora' semantics represented by today's own date — null only for 'mantenimiento' (no end date to compute from) or still busy past the scan horizon. Always anchored to the REAL today, independent of `year`. */
  nextAvailableDate: z.string().nullable(),
});
export type FleetAvailabilityAssetRow = z.infer<typeof FleetAvailabilityAssetRowSchema>;

export const FleetAvailabilitySummarySchema = z.object({
  disponibles: z.number().int().nonnegative(),
  alquilados: z.number().int().nonnegative(),
  reservados: z.number().int().nonnegative(),
  mantenimiento: z.number().int().nonnegative(),
  /** The browsed calendar year — navigable, independent of asset status/nextAvailableDate (always anchored to the real today). Defaults to the current year. */
  year: z.number().int(),
  assets: z.array(FleetAvailabilityAssetRowSchema),
});
export type FleetAvailabilitySummary = z.infer<typeof FleetAvailabilitySummarySchema>;

// ---------------------------------------------------------------------------
// Full response
// ---------------------------------------------------------------------------

export const LocxDashboardResponseSchema = z.object({
  generatedAt: z.string().datetime().or(z.date()),
  fleetStatus: FleetStatusCountsSchema,
  /** % of non-out-of-service units at compliance level 'verde'. null when fleetStatus.total - outOfService === 0. */
  complianceRate: z.number().min(0).max(100).nullable(),
  /** % of all tracked vehicle_document rows currently 'vigente'. null when there are no documents at all. */
  documentsUpToDatePercent: z.number().min(0).max(100).nullable(),
  servicesActiveCount: z.number().int().nonnegative(),
  documentsUpcomingExpirations: UpcomingExpirationBucketsSchema,
  documentsUpcomingExpirationsByType: z.array(DocumentTypeExpirationCountSchema),
  documentsUpcoming: z.array(UpcomingDocumentRowSchema),
  checklistOverdueCount: z.number().int().nonnegative(),
  criticalAlerts: CriticalAlertsSchema,
  servicesByState: ServiceStateCountsSchema,
  servicesCreatedLast7Days: z.array(DailyServiceCountSchema),
  fleetByLocality: z.array(FleetLocalityCountSchema),
  fleetByLocalityVehicles: z.array(FleetLocalityVehicleSchema),
  fleetUnassignedLocalityCount: z.number().int().nonnegative(),
  fleetByCostCenter: z.array(FleetCostCenterCountSchema),
  fleetUnassignedCostCenterCount: z.number().int().nonnegative(),
  attentionRequired: z.array(AttentionRequiredRowSchema),
  fuelConsumption: FuelConsumptionSummarySchema,
  fleetAvailability: FleetAvailabilitySummarySchema,
});
export type LocxDashboardResponse = z.infer<typeof LocxDashboardResponseSchema>;
